<template>
    <div class="billing-address-box">
        <p class="billing-address">
            <span class="billing-address-title">{{ $t( 'cart.billing_address' ) }}</span>
            <span class="edit-address" v-if="!expandAddress" @click="editAddress">{{ $t( 'cart.edit' ) }}</span>
        </p>
        <div class="billing-address-edit" v-show="expandAddress">
            <countrySelectSpinner :is-traffic="false"
                :supportedCountryList='supportedCountryList'
                @countrySelect="updateCountry"
                :selectedCountry="selectedCountry"
                @cancelErr="disableError('country')"
                :error="countryError"
                :disabled="isCountrySelectDisabled"
            />
            <stateSelectSpinner v-show="stateList.length"
                ref="stateSelector"
                :list="stateList"
                :selectedState="selectedState"
                @stateSelect="updateState"
                @cancelErr="disableError('state')"
                :error="stateError"></stateSelectSpinner>
            <div v-show="!stateList.length" class="reo-vfx-area" :class="isMobile?'':'half-width'">
                <label class="reo-vfx-box">
                    <input type="text"
                        :class="['reo-vfx-input', { empty: selectedState === '', error: stateError }]"
                        name="state"
                        :value="selectedState"
                        @change="inputChange($event, 'state')"
                        autocomplete="address-level1"
                        @focus="disableError('state')">
                    <span class="reo-vfx-placeholder">{{ $t( 'cart.address.state' ) }}</span>
                    <span class="reo-vfx-required-flag">*</span>
                </label>
            </div>
            <div v-for="(item, key) in addressDetails"
                :key="key"
                :name="key"
                :class="['reo-vfx-area', { 'half-width': item.half }]">
                <label class="reo-vfx-box">
                    <input type="text"
                        :class="['reo-vfx-input', { empty: item.value === '', error: item.error }]"
                        :value="item.value"
                        @change="inputChange($event, key)"
                        :autocomplete="item.autocomplete"
                        @focus="disableError('address', key)">
                    <span class="reo-vfx-placeholder">{{ item.placeholder }}</span>
                    <span v-if="item.required" class="reo-vfx-required-flag">*</span>
                </label>
            </div>
            <template v-if='VATenabled'>
                <div v-for="(item, key) in vatDetails"
                    :key="key"
                    :name="key"
                    :class="['reo-vfx-area', { 'half-width': item.half }]" >
                    <label class="reo-vfx-box">
                        <input type="text"
                            :class="['reo-vfx-input', { empty: item.value === '', error: item.error }]"
                            :value="item.value"
                            @change="inputChange($event, key)"
                            @focus="disableError('vat', key)">
                        <span class="reo-vfx-placeholder">{{ item.placeholder }}</span>
                        <span v-if="item.required" class="reo-vfx-required-flag">*</span>
                    </label>
                    <p v-if='item.error' style='font-size:16px;color:red'>{{item.error}}</p>
                </div>
            </template>
        </div>
        <div class="billing-address-show" v-show="!expandAddress">
            <p>{{ addressDetails.firstName.value }} {{ addressDetails.lastName.value }}</p>
            <p>{{ addressDetails.line1.value }}, {{ addressDetails.line2.value }}</p>
            <p>{{ addressDetails.city.value }}, {{ selectedState }}</p>
            <p>{{ selectedCountry }}, {{ addressDetails.postalCode.value }}</p>
            <template v-if='VATenabled'>
                <p>{{vatDetails.vatNumber.value}}</p>
                <p>{{vatDetails.vatCompany.value}}</p>
            </template>
        </div>
        <ReoDialog
            v-if="showItems.changeDefaultDialog"
            :description="$t('cart.address.save_address_dialog')"
            :label-of-confirm="$t('cart.address.confirm')"
            :label-of-cancel="$t('cart.address.cancel')"
            @confirm="showItems.changeDefaultDialog =false"
            @cancel="setDefault=false,showItems.changeDefaultDialog=false"
        />

    </div>
</template>

<script lang="ts">
import countrySelectSpinner from './components/countrySelectSpinner.vue';
import stateSelectSpinner from './components/stateSelectSpinner.vue';
import { ECommonButtonSize, ECommonButtonType, TAddress, TSelectionAddress } from '@reolink-cloud/interface';
import { String as StringUtils, Object as ObjectUtils } from '@reolink-web/utils';
import Vue, { PropType } from 'vue';
import ReoDialog from '@/basic/Dialog.vue';
import { isVATenabled, isVATValid } from '@/api/cart';
import { unfoldError } from '@/common/js/notice';
import { AxiosError } from 'axios';
import { states } from '@/config/states';
const { deepClone } = ObjectUtils;

interface IDataItem {
    value: string;
    placeholder: string;
    required: boolean;
    half: boolean;
    autocomplete: string;
    error: boolean | string;
}

type TDataDetail=Record<string, IDataItem>;

export default Vue.extend({
    components: {
        countrySelectSpinner,
        stateSelectSpinner,
        ReoDialog
    },
    props: {
        defaultExpandAddress: {
            type: Boolean,
            default: false
        },
        cookiesAddress: {
            type: Object as PropType<Record<string, any> | null>,
        },
        supportedCountryList: {
            type: Array as PropType<any[]>
        },
        countryError: {
            type: Boolean,
            default: false
        },
        stateError: {
            type: Boolean,
            default: false
        },
        addressDetails: {
            type: Object as PropType<TDataDetail>
        },
        vatDetails: {
            type: Object as PropType<TDataDetail>
        },
        selectedCountry: {
            type: String
        },
        selectedState: {
            type: String
        },
        defaultAddress: {
            type: String as PropType<'billing' | 'shipping' | null>
        },
        VATenabled: Boolean,
        isCountrySelectDisabled: Boolean

    },
    data() {

        return {
            ECommonButtonSize,
            ECommonButtonType,
            expandAddress: this.defaultExpandAddress,

            showItems: {
                selectionDialog: false,
                changeDefaultDialog: false
            },
            setDefault: false,
            selectedAddress: null as TSelectionAddress,
            stateListRes: [] as any[],
        };

    },
    computed: {
        stateList(): any[] {

            if ( Object.keys( this.stateListRes ).length > 0 ) {

                return Object.entries( this.stateListRes ).map(( [key, value] ) => ({
                    key,
                    name: value
                }));

            } else {

                return [];

            }

        },
        isMobile ( ): boolean {

            return  this.$store.getters.isMobile;

        },
    },
    methods: {
        /**
         * 在对地址进行修改后设置地址选择器无选中地址
         */
        async changeWrapper( fn: () => any ) {

            await fn();
            this.selectedAddress = null;

        },
        async updateCountry( country: string ): Promise<void> {

            await this.changeWrapper(() => {

                this.$emit( 'updateCountry', country );

            });

        },
        editAddress (): void {

            this.expandAddress = true;

        },

        async inputChange ( e, key ): Promise<void> {

            await this.changeWrapper( async () => {

                if ( key === 'state' ) {

                    this.$emit( 'updateStates', StringUtils.trim( e.target.value ));

                }
                else if ( key === 'vatNumber' ) {

                    await this.setVatNumber( e.target.value );

                }
                else if ( key === 'vatCompany' ) {

                    this.$emit( 'vatChange', 'vatCompany', e.target.value );

                }
                else if ( key in this.addressDetails ) {

                    this.$emit( 'addressChange', key, e.target.value );

                }

            });

        },
        compareCurrentWithAddress( address: TAddress ) {

            for ( const key of Object.keys( this.addressDetails )) {

                if ( address[key] !== this.addressDetails[key].value ) {

                    return false;

                }

            }
            return true;

        },
        async selectAddress( address: TAddress ) {

            this.$emit( 'updateWatchFlag', false );
            if ( address.country !== this.selectedCountry ) {

                this.stateListRes = [];
                this.$emit( 'updateCountry', address.country );

            }
            this.$emit( 'updateStates', address.state );
            const newAddressDetails = deepClone( this.addressDetails );
            for ( const key in address ) {

                if ( key in newAddressDetails ) {

                    newAddressDetails[key].value = address[key];

                }

            }
            if ( 'company' in address ) {

                this.$emit( 'vatChange', 'vatCompany', address.company );

            }
            this.$emit( 'addressChange', 'all', newAddressDetails );
            this.disableError( 'country' );
            this.disableError( 'state' );
            for ( const key in this.addressDetails ) {

                this.disableError( 'address', key );

            }
            for ( const key in this.vatDetails ) {

                this.disableError( 'vat', key );

            }
            this.$emit( 'updateWatchFlag', true );
            if ( 'vatNumber' in address && address.vatNumber ) {

                await this.setVatNumber( address.vatNumber );

            }

        },
        disableError( type: 'country'|'state'|'vat'|'address', key?: string ) {

            switch ( type ) {

                case 'country':
                    this.$emit( 'disableCountryError' );
                    break;
                case 'state':
                    this.$emit( 'disableStateError' );
                    break;
                case 'vat':
                    this.$emit( 'disableVatError', key );
                    break;
                case 'address':
                    this.$emit( 'disableAddressError', key );
                    break;

            }

        },
        async setVatNumber( vatNumber: string ) {

            if ( vatNumber === '' ) {

                this.$emit( 'vatChange', 'vatNumber', vatNumber );
                return;

            }

            try {

                this.$emit( 'updateLoading', true );
                let res = await isVATValid( vatNumber );
                if ( res.valid ) {

                    if ( res.company !== '' ) {

                        this.$emit( 'vatChange', 'vatCompany', res.company );

                    }
                    if ( res.address !== '' ) {

                        this.$emit( 'addressChange', 'line1', res.address );

                    }
                    this.$emit( 'addressChange', 'line2', '' );
                    this.$emit( 'vatValid', true );

                } else {

                    this.$emit( 'vatError', 'vatNumber', this.$tc( 'tax.vatNumberNotValid' ));
                    this.$emit( 'vatValid', false );

                }

            } catch ( err ) {

                const symbol = ( err as AxiosError ).response?.data.error.symbol;
                if ( symbol === 'vat_number_not_valid' ) {

                    this.$emit( 'vatError', 'vatNumber', this.$tc( 'tax.vatNumberNotValid' ));
                    this.$emit( 'vatValid', false );

                } else {

                    console.error( err );
                    unfoldError( 'errors.server_error' );

                }

            } finally {

                this.$emit( 'updateLoading', false );
                this.$emit( 'vatChange', 'vatNumber', vatNumber );

            }

        },
        async updateState( state: string ) {

            await this.changeWrapper(() => {

                this.$emit( 'updateStates', state );

            });

        }

    },
    watch: {
        defaultExpandAddress( newVal ) {

            this.expandAddress = newVal;

        },
        selectedCountry: {
            async handler( newVal ) {

                if ( newVal === '' ) {

                    return ;

                }

                if ( !this.supportedCountryList.some( country => country.code === newVal )) {

                    this.$emit( 'updateCountry', '' );
                    this.expandAddress = true;
                    return;

                }

                this.$emit( 'vatEnabledChange', await isVATenabled( newVal ));
                this.stateListRes = ( states[newVal] ?? [] ).map( state => state.name );

            },
            immediate: true

        },
        defaultSelectedAddress: {
            handler( newVal, oldVal ) {

                if ( !oldVal && newVal ) {

                    this.selectedAddress = newVal;

                }

            },
            immediate: true
        }
    }

});

</script>

<style lang="less" scoped>
.billing-address {
    margin-bottom: 36px;
    @media @snapeak_mobile {
        margin-bottom: .4rem;
        display: flex;
        flex-direction: column;
        gap: 4px;
        width: 162px;
    }
    .address-btn{
        margin-left: 10px;
        @media @snapeak_mobile{
            margin-left: 0;
        }
    }

    span {

        &.billing-address-title {
            display: inline-block;
            font-size: 20px;
            font-weight: 700;
            color: @font_primary;
            @media @snapeak_mobile {
                font-size: .36rem;
            }
        }

        &.edit-address {
            float: right;
            font-size: 16px;
            font-weight: 500;
            color: @brand_light;
            cursor: pointer;
            @media @snapeak_mobile {
                font-size: .26rem;
                position: absolute;
                right: 0;
                top: 0;
            }
        }
    }
}
.billing-address-box {
    margin-bottom: 54px;
    @media @snapeak_mobile {
        margin-bottom: 10px;
        position: relative;
    }

    .billing-address-edit {
        font-size: 0;
        @media @snapeak_mobile {
            /deep/ .country-select-spinner{
                z-index: 9;
            }
        }
    }
    .billing-address-show {
        padding: 18px 20px;
        font-size: 16px;
        font-weight: 500;
        color: @font_secondary;
        border: 1px solid #CCCCCC;
        @media @snapeak_mobile {
            padding: .3rem .4rem;
            font-size: .28rem;
        }

        p + p {

            margin-top: 12px;
        }
    }

    .saveDefault{
        display: flex;
        gap: 10px;
        margin-top: 10px;
        align-items: flex-end;
        cursor: pointer;
        user-select: none;
        width: fit-content;
    }
}

.reo-vfx-area {
    display: inline-block;
    width: 100%;
    margin-bottom: 20px;

    &.half-width {
        width: calc((100% - 20px) / 2);

        &:nth-child(even) {
            margin-left: 20px;
        }
    }

    .reo-vfx-box {
        position: relative;
        display: block;
    }

    .reo-vfx-input {
        font-weight: 500;
        font-size: 16px;
        padding: 0 20px;
        width: 100%;
        height: 48px;
        line-height: 48px;
        box-sizing: border-box;
        box-shadow: none;
        outline: none;
        border: 1px solid transparent;
        border-radius: 6px;
        box-shadow: 0 0 6px 2px rgba(0,0,0,.14);
        background-color: transparent;
        @media @snapeak_mobile {
            font-size: .28rem;
        }

        &:focus {
            background-color: transparent;
            border-color: @brand_light;
        }
        &.error {
            border-color: @error;
        }

    }

    .reo-vfx-placeholder {
        white-space: nowrap;
        transition: all 0.3s ease-in-out;
        position: absolute;
        top: 50%;
        left: 20px;
        transform: translateY(-50%);
        pointer-events: none;
        font-size: 16px;
        font-weight: 500;
        display: inline-block;
        padding: 0 4px;
        color: @font_secondary;
        @media @snapeak_mobile {
            font-size: .28rem;
        }
    }

    .reo-vfx-input:focus ~ .reo-vfx-placeholder,
    .reo-vfx-input:not(.empty) ~ .reo-vfx-placeholder {
        top: 0;
        font-size: 14px;
        color: @b_6;
        background-color: @bg_02;
        @media @snapeak_mobile {
            font-size: .24rem;
            background-color: @bg_01;
        }
    }

    .reo-vfx-input:focus ~ .reo-vfx-placeholder {
        color: @b_6;
    }

    .reo-vfx-required-flag {
        position: absolute;
        top: 0;
        right: -10px;
        color: #e74330;
        font-weight: 500;
        font-size: 18px;
    }

    .input-tip {
        font-size: 14px;
        color: #777;
        font-weight: 500;
        line-height: 1.2;
        margin-top: 16px;
    }
}
@media @snapeak_mobile {
    .reo-vfx-area.half-width[name='postalCode'] {
        margin-left: 0;
        width: 100%;
    }
}

</style>
